
import { useEffect, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import 'swiper/css';
import 'swiper/css/pagination';
import api from '../../api/api';
import { useProfileStore } from '../../store/useUserStore';
import CourseCard from '../../components/Course/CourseCard';
import { useTranslation } from 'react-i18next';
import { extractYouTubeId, isColorDark } from '../../helpers/helper';

const ExploreBranchView = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { branchId } = useParams()
    const { setLoading } = useProfileStore()
    const [branch, setBranch] = useState<any>({
        name: "",
        description: "",
        branch_location: {},
        images: [],
        course_list: [],
        all_categories: [],
        dean_coach: null,
        teachers_list: []
    })
    const [selectedCategory, setSelectedCategory] = useState<string>('all')
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
    const [showTeacherModal, setShowTeacherModal] = useState(false)
    const [selectedTeacher, setSelectedTeacher] = useState<any>(null)

    const navigateBack = () => {
        if (window.history.length > 1) {
            navigate(-1) // Go back to previous page
        } else {
            navigate('/?tab=explore', { replace: true })
        }
    }

    // Generate SEO data for the branch
    const generateSEOData = () => {
        const city = branch.branch_location?.city || ''
        const state = branch.branch_location?.state || ''
        const location = city && state ? `${city}, ${state}` : ''
        
        const title = location 
            ? `${branch.name} at ${location} | MYKOKU`
            : `${branch.name} | MYKOKU`
        
        const description = location
            ? `Discover ${branch.name} at ${location}. Find courses, teachers, and more information about this academy. Powered by MYKOKU.`
            : `Discover ${branch.name}. Find courses, teachers, and more information about this academy. Powered by MYKOKU.`
        
        const canonicalUrl = `${window.location.origin}/explore/branch/${branchId}`
        
        return {
            title,
            description,
            canonicalUrl,
            imageUrl: branch.academy_images || branch.images?.[0] || `${window.location.origin}/assets/img/products/product1.png`
        }
    }


    useEffect(() => {
        if (branchId) {
            fetchBranchDetails()
        }
    }, [branchId])

    const fetchBranchDetails = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/parent/explore/details?type=branch&branch_id=${branchId}`)
            if (response.data && response.data.data) {
                setBranch(response.data.data)
            }
        } catch (error) {
            console.error('Error fetching branch details:', error)
        } finally {
            setLoading(false)
        }
    }

    const filteredCourses = selectedCategory === 'all' 
        ? branch.course_list.flatMap((category: any) => category.courses)
        : branch.course_list
            .filter((category: any) => category.category.id.toString() === selectedCategory)
            .flatMap((category: any) => category.courses)

    return (
        <div className="pb-20 px-2">
            {/* SEO Meta Tags */}
            {branch.name && (
                <Helmet>
                    <title>{generateSEOData().title}</title>
                    <meta name="description" content={generateSEOData().description} />
                    <link rel="canonical" href={generateSEOData().canonicalUrl} />
                    
                    {/* Open Graph tags for Facebook/WhatsApp */}
                    <meta property="og:title" content={generateSEOData().title} />
                    <meta property="og:description" content={generateSEOData().description} />
                    <meta property="og:image" content={generateSEOData().imageUrl} />
                    <meta property="og:url" content={generateSEOData().canonicalUrl} />
                    <meta property="og:type" content="website" />
                    <meta property="og:site_name" content="MYKOKU" />
                    
                    {/* Twitter Card tags */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={generateSEOData().title} />
                    <meta name="twitter:description" content={generateSEOData().description} />
                    <meta name="twitter:image" content={generateSEOData().imageUrl} />
                    
                    {/* Additional meta tags */}
                    <meta name="robots" content="index, follow" />
                    <meta name="author" content="MYKOKU" />
                </Helmet>
            )}

            {/* Header */}
            <div className='mb-3 -mx-6'>
                {/* Back arrow above image */}
                <div className='flex items-center p-2'>
                    <IoIosArrowBack size={28} color='#00d3a0' onClick={navigateBack}/>
                </div>
                
                {/* Full width banner background */}
                {branch.cover_image ? (
                    <img 
                        src={branch.cover_image} 
                        alt="Banner" 
                        className="h-48 object-cover"
                        style={{ width: '100vw' }}
                    />
                ) : (
                    <div 
                        className="h-48 bg-gray-300"
                    />
                )}
            </div>

            {/* Branch Info Card */}
            <div className="bg-white mb-4 overflow-hidden sticky top-0 z-50 -mx-2 px-2">
                {/* Branch Header */}
                <div className="py-4 pb-0">
                    <div className="flex items-center mb-3">
                        {/* Branch Logo */}
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-3 overflow-hidden">
                            {branch.academy_images ? (
                                <img 
                                    src={branch.academy_images} 
                                    alt={branch.name} 
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-8 h-8 bg-purple-500 rounded flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">
                                        {branch.name?.charAt(0) || 'B'}
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        {/* Branch Info */}
                        <div className="flex-1">
                            <h1 className="text-lg font-bold text-gray-900 mb-1">
                                {branch.name}
                            </h1>
                            {branch.branch_location && (
                                <div className="flex items-center text-gray-600 text-sm">
                                    <span className="text-pink-500 mr-1">📍</span>
                                    {branch.branch_location.city}, {branch.branch_location.state}
                                </div>
                            )}
                        </div>
                        
                        {/* Share Icon */}
                        {/* <button className="p-2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                            </svg>
                        </button> */}
                    </div>
                    
                    {/* Category Tags and Powered by MyKoku */}
                    <div className="flex items-center justify-between mb-4">
                        {/* Category Tags */}
                        <div className="flex flex-wrap gap-2 flex-1">
                            {branch.all_categories?.map((category: any) => (
                                <div 
                                    key={category.id}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-base"
                                    style={{ 
                                        backgroundColor: category.color ? `#${category.color}` : '#F3F4F6',
                                        color: category.color && isColorDark(category.color) ? 'white' : 'black'
                                    }}
                                >
                                    {category.image && (
                                        <img 
                                            src={category.image} 
                                            alt={category.name} 
                                            className="w-3 h-3 mr-1"
                                            style={{
                                                filter: category.color && isColorDark(category.color) ? 'brightness(0) invert(1)' : 'none'
                                            }}
                                        />
                                    )}
                                    {category.name}
                                </div>
                            ))}
                        </div>
                        
                        {/* Powered by MyKoku */}
                        <div className="text-xs text-gray-400 ml-4 whitespace-nowrap">
                            {t("explore.powered_by")} <span className="font-semibold text-theme">MYKOKU</span>
                        </div>
                    </div>
                </div>
                

                {/* Removed the separate Powered by MyKoku section */}
            </div>

            {/* Navigation Tabs */}
            <div className="flex justify-evenly border-b border-gray-200 mb-4">
                <button 
                    onClick={() => {
                        const element = document.getElementById("detail-section");
                        if (element) {
                            const yOffset = -120; // Offset for sticky header
                            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({top: y, behavior: 'smooth'});
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium border-b-2 border-theme text-theme"
                >
                    {t("explore.detail")}
                </button>
                <button 
                    onClick={() => {
                        const element = document.getElementById("courses-section");
                        if (element) {
                            const yOffset = -120; // Offset for sticky header
                            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({top: y, behavior: 'smooth'});
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                    {t("explore.courses")}
                </button>
                <button 
                    onClick={() => {
                        const element = document.getElementById("teachers-section");
                        if (element) {
                            const yOffset = -120; // Offset for sticky header
                            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({top: y, behavior: 'smooth'});
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                    {t("explore.teachers")}
                </button>
            </div>

            {/* Detail Section Anchor */}
            <div id="detail-section"></div>

            {/* YouTube Embed */}
            {branch.branch_contact?.link && (branch.branch_contact.link.includes('youtu.be') || branch.branch_contact.link.includes('youtube.com')) && (
                <div className="mb-6">
                    <div className="rounded-xl overflow-hidden">
                        <iframe
                            width="100%"
                            src={`https://www.youtube.com/embed/${extractYouTubeId(branch.branch_contact.link)}`}
                            title="Academy Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-48 md:h-64"
                        />
                    </div>
                </div>
            )}

            
            {/* Branch Description */}
            {branch.description && (
                <div className="mb-6">
                    <div className="text-gray-700 text-sm leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: branch.description }} />
                    </div>
                </div>
            )}
            {/* Branch Images */}
            {branch.images && branch.images.length > 0 && (
                <div className="mb-6">
                    <div className="rounded-xl overflow-visible h-48 md:h-64 mb-4">
                        <Swiper
                            initialSlide={currentImageIndex}
                            pagination={{
                                dynamicBullets: true,
                                clickable: true,
                                el: '.swiper-pagination-custom',
                            }}
                            modules={[Pagination]}
                            className="h-full"
                            slidesPerView={1.2}
                            centeredSlides={true}
                            spaceBetween={16}
                            onSlideChange={(swiper) => {
                                setCurrentImageIndex(swiper.realIndex);
                            }}
                        >
                            {branch.images.map((img: string, index: number) => (
                                <SwiperSlide key={index}>
                                    <div className="flex justify-center items-center h-full">
                                        <img 
                                            src={img} 
                                            alt={`${branch.name} ${index + 1}`} 
                                            className="w-full h-full object-cover rounded-lg" 
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                    {/* Custom pagination container positioned below image */}
                    <div className="swiper-pagination-custom flex justify-center"></div>
                </div>
            )}
            <div className='mt-2' id="courses-section"></div>

            {/* Category Filter */}
            {branch.all_categories && branch.all_categories.length > 0 && (
                <div className="pt-2 mb-4">
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                                selectedCategory === 'all' 
                                    ? 'bg-theme text-white' 
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {t("explore.All")}
                        </button>
                        {branch.all_categories.map((category: any) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id.toString())}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                                    selectedCategory === category.id.toString()
                                        ? 'bg-theme text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                            {t("explore."+category.name)}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Courses Grid */}
            <div>
                <div className="grid grid-cols-2 gap-4">
                    {filteredCourses.map((course: any) => {
                        // Transform schedules to get day array
                        const dayMap: { [key: number]: string } = {
                            1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun'
                        }
                        const concatDayArray: string[] = Array.from(new Set(
                            course.schedules?.map((schedule: any) => 
                                dayMap[schedule.day?.value] || schedule.day?.label || ''
                            ).filter((day: string) => day) || []
                        ))

                        return (
                            <CourseCard
                                explore={false}
                                key={course.course_branch_id}
                                course_id={course.course_branch_id}
                                name={course.name}
                                concatDayArray={concatDayArray}
                                images={course.image ? [course.image] : ['/assets/img/products/product1.png']}
                                navigateTo={`/explore/course/${course.course_branch_id}`}
                            />
                        )
                    })}
                </div>
            </div>

            <div id="teachers-section"></div>
            {/* Our Teachers Section */}
            {(branch.dean_coach || (branch.teachers_list && branch.teachers_list.length > 0)) && (
                <div className="mt-8">
                    <div className={`rounded-xl p-4 text-white relative overflow-hidden ${!branch.academy_images ? 'bg-gradient-to-br from-purple-600 to-indigo-700' : ''}`}>
                        {/* Background Image or Pattern */}
                        {branch.academy_images ? (
                            <>
                                <div 
                                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                                    style={{ backgroundImage: `url(${branch.academy_images})` }}
                                />
                                <div className="absolute inset-0 bg-black opacity-60"></div>
                            </>
                        ) : (
                            <div className="absolute inset-0 opacity-20">
                                <div className="absolute top-4 right-4 w-32 h-32 rounded-full bg-white opacity-10"></div>
                                <div className="absolute bottom-4 left-4 w-24 h-24 rounded-full bg-white opacity-10"></div>
                            </div>
                        )}
                        
                        {/* Header */}
                        <div className="relative z-10 mb-6">
                            <div className="flex items-center mb-2">
                                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                                <h2 className="text-xl font-bold">{t("explore.our_teachers")}</h2>
                            </div>
                            {/* Underline */}
                            <div className="w-full h-0.5 bg-white opacity-80"></div>
                        </div>

                        {/* Dean Section */}
                        {branch.dean_coach && (
                            <div className="relative z-10 mb-6">
                                <h3 className="text-lg font-semibold mb-3 text-gray-200">{t("explore.dean")}</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div 
                                        className="text-center cursor-pointer"
                                        onClick={() => {
                                            setSelectedTeacher(branch.dean_coach)
                                            setShowTeacherModal(true)
                                        }}
                                    >
                                        <div className="relative inline-block">
                                            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-theme bg-gray-200 mx-auto">
                                                <img 
                                                    src={branch.dean_coach.profile_pic || '/assets/img/default-profile.jpg'} 
                                                    alt={branch.dean_coach.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-2 text-xs font-medium text-white text-center leading-tight">
                                            {branch.dean_coach.name.length > 15 ? `${branch.dean_coach.name.substring(0, 15)}...` : branch.dean_coach.name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Teachers Grid */}
                        {branch.teachers_list && branch.teachers_list.length > 0 && (
                            <div className="relative z-10">
                                <h3 className="text-lg font-semibold mb-3 text-gray-200">{t("explore.teachers")}</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    {branch.teachers_list.slice(0, 6).map((teacher: any, index: number) => (
                                        <div 
                                            key={teacher.id} 
                                            className="text-center cursor-pointer"
                                            onClick={() => {
                                                setSelectedTeacher(teacher)
                                                setShowTeacherModal(true)
                                            }}
                                        >
                                            <div className="relative inline-block">
                                                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-theme bg-gray-200 mx-auto">
                                                    <img 
                                                        src={teacher.profile_pic || '/assets/img/default-profile.jpg'} 
                                                        alt={teacher.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-2 text-xs font-medium text-white text-center leading-tight">
                                                {teacher.name.length > 15 ? `${teacher.name.substring(0, 15)}...` : teacher.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Show more teachers if there are more than 6 */}
                                {branch.teachers_list.length > 6 && (
                                    <div className="text-center mt-4">
                                        <button className="text-sm text-gray-200 hover:text-white transition-colors">
                                            +{branch.teachers_list.length - 6} {t("explore.more_teachers")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Want to know more Section */}
            <div className="my-6">
                <div className="rounded-xl p-8 text-center">
                    {/* Question Mark Icon */}
                    <div className="flex justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                    </div>
                    
                    {/* Text */}
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                        {t("explore.want_to_know_more")}
                    </h3>
                    
                    {/* WhatsApp Button */}
                    <button 
                        className="bg-theme hover:bg-green-600 text-white px-8 py-3 rounded-full flex items-center justify-center mx-auto transition-colors duration-200 shadow-md"
                        onClick={() => {
                            // Use the specific WhatsApp API format with predefined message
                            const phoneNumber =  branch.branch_contact?.contact ? "60" + branch.branch_contact?.contact : ""
                            const message = encodeURIComponent(`Hello I click the link from MYKOKU, I am interested in ${branch.name}`)
                            const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`
                            window.open(whatsappUrl, '_blank');
                        }}
                    >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                        {t("explore.contact_us_via_whatsapp")}
                    </button>
                </div>
            </div>


            {/* Recommended Courses Section */}
            {branch.recommended_courses && branch.recommended_courses.length > 0 && (
                <div className="pb-6">
                    <div className="mb-4">
                        {/* Recommended for you title */}
                        <h3 className="text-base font-medium text-gray-500 text-center">
                            {t("explore.recommended_for_you")}
                        </h3>
                        {/* Header with course type and back button */}
                        <div className="flex items-center justify-between my-4">
                            {/* Course Type Tag */}
                            {branch.course_type && (
                                <div 
                                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
                                    style={{ 
                                        backgroundColor: branch.course_type.color ? `#${branch.course_type.color}` : '#FED7AA',
                                        color: branch.course_type.color && isColorDark(branch.course_type.color) ? 'white' : 'black'
                                    }}
                                >
                                    {branch.course_type.icon ? (
                                        <img 
                                            src={branch.course_type.icon} 
                                            alt={branch.course_type.name} 
                                            className="w-5 h-5 mr-2"
                                            style={{
                                                filter: branch.course_type.color && isColorDark(branch.course_type.color) ? 'brightness(0) invert(1)' : 'none'
                                            }}
                                        />
                                    ) : (
                                        <span className="mr-2">🎨</span>
                                    )}
                                    {branch.course_type.name}
                                </div>
                            )}
                            
                            {/* Back to explore button */}
                            <button 
                                onClick={() => navigate('/?tab=explore')}
                                className="flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                                <svg 
                                    className="w-4 h-4 mr-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M15 19l-7-7 7-7" 
                                    />
                                </svg>
                                {t("explore.back_to_explore")}
                            </button>
                        </div>
                        
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        {branch.recommended_courses.map((recommendedCourse: any, index: number) => {
                            // Transform schedules to get day array
                            const dayMap: { [key: number]: string } = {
                                1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun'
                            }
                            const concatDayArray: string[] = Array.from(new Set(
                                recommendedCourse.schedules?.map((schedule: any) => 
                                    dayMap[schedule.day?.value] || schedule.day?.label || ''
                                ).filter((day: string) => day) || []
                            ))

                            return (
                                <CourseCard
                                    explore={false}
                                    key={recommendedCourse.course_branch_id || index}
                                    course_id={recommendedCourse.course_branch_id}
                                    name={recommendedCourse.course_name}
                                    concatDayArray={concatDayArray}
                                    images={recommendedCourse.images}
                                    navigateTo={`/explore/course/${recommendedCourse.course_branch_id}`}
                                />
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Teacher/Dean Profile Modal */}
            <AnimatePresence>
                {showTeacherModal && selectedTeacher && (
                    <div 
                        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-end" 
                        onClick={() => setShowTeacherModal(false)}
                        style={{ touchAction: 'none' }}
                    >
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ stiffness: 100 }}
                            drag="y"
                            dragConstraints={{ top: 0, bottom: 0 }}
                            dragElastic={0.2}
                            onDragEnd={(_, info) => {
                                if (info.offset.y > 100 || info.velocity.y > 500) {
                                    setShowTeacherModal(false)
                                }
                            }}
                            className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-6 pt-6 pb-16 touch-none"
                            onClick={(e) => e.stopPropagation()}
                            style={{ touchAction: 'none' }}
                        >
                            {/* Drag Handle */}
                            <div className="flex justify-center mb-4">
                                <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                            </div>

                            {/* Profile Section */}
                            <div className="text-center mb-6">
                                {/* Profile Image */}
                                <div className="relative inline-block mb-4">
                                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 mx-auto border-4 border-theme">
                                        <img 
                                            src={selectedTeacher.profile_pic || '/assets/img/default-profile.jpg'} 
                                            alt={selectedTeacher.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>

                                {/* Name */}
                                <h2 className="text-xl font-bold text-gray-900 mb-2">
                                    {selectedTeacher.name}
                                </h2>
                            </div>

                            {/* Bio & Teacher's Words Section */}
                            <div className="space-y-4 overflow-y-auto max-h-96" style={{ touchAction: 'pan-y' }}>
                                {/* Bio Section */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                        {t("explore.bio")}
                                    </h3>
                                    
                                    {/* Bio Content */}
                                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                        {/* Default bio if not provided in API */}
                                        <p className="text-gray-700 text-sm leading-relaxed">
                                            {selectedTeacher.bio || t("explore.default_bio")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

        </div>
    )
}

export default ExploreBranchView